

import React, { useState, useEffect, SyntheticEvent } from 'react';
import styled from 'styled-components';

import type {
  EmailReminder,
  PatchMessageConfigurationValuesRequest,
} from '../../types/messageConfiguration';
import {
  InputRowWithTextInput,
  InputRowWithEmailInput,
  InputRowWithTextArea,
} from './InputRow';
import type { StyledComponentWithChildren } from '../../types/styledComponent';
import ActionRow from './ActionRow';
import { encodeToBase64 } from '../../lib/encodeToBase64';

const FormCard: StyledComponentWithChildren = styled.div`
  display: flex;
  flex-direction: column;
`;

interface EmailReminderProps {
  emailReminder?: EmailReminder;
  domainList: string[];
  emailList: string[];
  onSave: (patchRequest: PatchMessageConfigurationValuesRequest) => void;
}

const EmailReminderForm: React.FC<EmailReminderProps> = ({
  emailReminder,
  domainList,
  emailList,
  onSave,
}) => {
  const [emailInput, updateEmailInput] = useState(emailReminder?.fromEmail || '');
  const [aliasInput, updateAliasInput] = useState(emailReminder?.fromAlias || '');
  const [headerInput, updateHeaderInput] = useState(emailReminder?.header || '');
  const [closingInput, updateClosingInput] = useState(emailReminder?.closing || '');

  const [emailInputMessage, updateEmailInputMessage] = useState<any>();

  // Though this may seem redundant, it's to allow for non-email causes of form error to be added in the below useEffect
  const [hasFormError, updateHasFormError] = useState(false);

  useEffect(() => {
    // if emailInputMessage is falsy or type !== error, set HasFormError false, else true
    updateHasFormError(emailInputMessage?.type === 'error');
  }, [emailInputMessage]);

  const namePrefix = 'emailReminder';

  const updateInputStateWithProps = (): void => {
    updateEmailInput(emailReminder?.fromEmail || '');
    updateAliasInput(emailReminder?.fromAlias || '');
    updateHeaderInput(emailReminder?.header || '');
    updateClosingInput(emailReminder?.closing || '');
  };

  const handleOnCancel = (): void => {
    updateInputStateWithProps();
  };

  useEffect(() => {
    updateInputStateWithProps();
  }, [emailReminder]);

  const updateMessageConfigValues = () => {
    const patchRequest: PatchMessageConfigurationValuesRequest = {
      messageConfigurationValues: {
        emailReminder: {
          ...(emailInput && { fromEmail: emailInput }),
          ...(aliasInput && { fromAlias: aliasInput }),
          ...(headerInput && { header: encodeToBase64(headerInput) }),
          ...(closingInput && { closing: encodeToBase64(closingInput) }),
        },
      },
    };
    onSave(patchRequest);
  };

  return (
    <FormCard>
      <ActionRow
        id={`${namePrefix}Top`}
        saveDisabled={hasFormError}
        onCancel={handleOnCancel}
        onSave={updateMessageConfigValues}
      />

      <InputRowWithEmailInput
        name={`${namePrefix}FromEmail`}
        labelText="From Email Address:"
        inputValue={emailInput}
        onChange={updateEmailInput}
        setDisplayMessage={updateEmailInputMessage}
        displayMessage={emailInputMessage}
        domainList={domainList}
        emailList={emailList}
      />

      <InputRowWithTextInput
        name={`${namePrefix}FromAlias`}
        labelText="From Alias:"
        inputValue={aliasInput}
        onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
          updateAliasInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Header`}
        labelText="Header:"
        inputValue={headerInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateHeaderInput(e.currentTarget.value);
        }}
      />

      <InputRowWithTextArea
        name={`${namePrefix}Closing`}
        labelText="Closing:"
        inputValue={closingInput}
        onChange={(e: SyntheticEvent<HTMLTextAreaElement>): void => {
          updateClosingInput(e.currentTarget.value);
        }}
      />

      <ActionRow
        id={`${namePrefix}Bottom`}
        saveDisabled={hasFormError}
        onCancel={handleOnCancel}
        onSave={updateMessageConfigValues}
      />
    </FormCard>
  );
};

export default EmailReminderForm;
